import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CircleX, Copy, Download } from 'lucide-react';
import {
  cancelManualOrder,
  fetchManualOrderCloneContext,
  createExport,
  getExportStatus,
  getManualOrderDocuments,
  listManualOrders,
} from '../../services/shipturtleApi';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import {
  manualOrderProgressLine,
  manualOrderStatusClass,
  manualOrderStatusLabel,
} from './manualOrderStatusBadge';
import './ExceptionLabelsTable.css';
import './ManualOrdersTable.css';

/**
 * Orders placed outside Shopify.
 *
 * Rows are whole orders that carry their own single label — unlike the
 * exception-labels list, where a row is one leg of an existing order. They have
 * no Shopify counterpart and no link to a terminal Order, which is why this is
 * its own tab rather than a filter on the orders list.
 *
 * The status pill and its tone are shared with the exception list on purpose:
 * both answer "where is this parcel?" from the same Prozo vocabulary, so they
 * must read identically.
 */
// 25 keeps a page scannable without scrolling, and matches the Orders tab.
const PAGE_SIZE = 25;

const FILTER_LABELS = {
  progress: 'Carrier scan',
  charge_bearer: 'Charge to',
  status: 'Status',
  payment_mode: 'Payment',
  vendor_ids: 'Vendor',
  carrier_ids: 'Carrier',
  order_number: 'Order',
  customer_name: 'Customer',
  customer_phone: 'Phone',
  awb_number: 'AWB',
  date_from: 'Created from',
  date_to: 'Created to',
  delivered_from: 'Delivered from',
  delivered_to: 'Delivered to',
};

/** Multi-select dropdown — same shape the exception filters use. */
function MultiSelect({ label, options, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const chosen = selected || [];
  return (
    <div className="exLabelsField">
      <label>{label}</label>
      <div className="exLabelsMulti" ref={ref}>
        <button type="button" className="exLabelsMultiTrigger" onClick={() => setOpen((v) => !v)}>
          <span>{chosen.length ? `${chosen.length} selected` : 'All'}</span>
          <span aria-hidden="true">▾</span>
        </button>
        {open && (
          <div className="exLabelsMultiMenu">
            {options.map((opt) => (
              <label key={opt.value} className="exLabelsMultiOption">
                <input
                  type="checkbox"
                  checked={chosen.includes(opt.value)}
                  onChange={(e) => onToggle(opt.value, e.target.checked)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Mirrors the backend ManualOrderStatus. Labels match its get_status_display()
// so a filter chip reads the same as the badge it selects.
const MANUAL_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'ready_to_ship', label: 'Ready to ship' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'exceptions', label: 'Exceptions' },
  { value: 'rto', label: 'Return to origin' },
  { value: 'held', label: 'Held' },
  { value: 'lost', label: 'Lost' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_MODES = [
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'COD', label: 'COD' },
];

const CHARGE_BEARERS = [
  { value: 'SUSPIRE', label: 'Suspire' },
  { value: 'BRAND', label: 'Brand' },
];

function FiltersModal({ open, applied, options, onClose, onApply }) {
  const [draft, setDraft] = useState(applied);

  useEffect(() => { if (open) setDraft(applied); }, [open, applied]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const toggleMulti = (key, value, checked) =>
    setDraft((prev) => {
      const current = prev[key] || [];
      const next = checked ? [...current, value] : current.filter((v) => v !== value);
      return { ...prev, [key]: next };
    });

  const apply = () => {
    // Drop empties so the chip bar and the request carry only real filters.
    const clean = {};
    Object.entries(draft).forEach(([key, value]) => {
      if (Array.isArray(value) ? value.length : value !== '' && value != null) {
        clean[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
    onApply(clean);
  };

  return (
    <div className="exLabelsModalBackdrop" onClick={onClose} role="presentation">
      <div
        className="exLabelsModal"
        role="dialog"
        aria-modal="true"
        aria-label="Filter manual orders"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="exLabelsModalHead">
          <div className="exLabelsModalTitle">Filters</div>
          <button type="button" className="exLabelsModalClose" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="exLabelsModalBody">
          <div className="exLabelsFilterGrid">
            <MultiSelect
              label="Order state"
              options={MANUAL_STATUSES}
              selected={draft.status}
              onToggle={(v, c) => toggleMulti('status', v, c)}
            />
            <MultiSelect
              label="Tracking status"
              options={options.shipment_progresses || []}
              selected={draft.progress}
              onToggle={(v, c) => toggleMulti('progress', v, c)}
            />
            <MultiSelect
              label="Payment"
              options={PAYMENT_MODES}
              selected={draft.payment_mode}
              onToggle={(v, c) => toggleMulti('payment_mode', v, c)}
            />
            <MultiSelect
              label="Charge to"
              options={CHARGE_BEARERS}
              selected={draft.charge_bearer}
              onToggle={(v, c) => toggleMulti('charge_bearer', v, c)}
            />
            <MultiSelect
              label="Carrier"
              options={(options.carriers || []).map((c) => ({
                value: String(c.id ?? c.value),
                label: c.name ?? c.label,
              }))}
              selected={draft.carrier_ids}
              onToggle={(v, c) => toggleMulti('carrier_ids', v, c)}
            />
            <MultiSelect
              label="Vendor"
              options={(options.vendors || []).map((v) => ({
                value: String(v.id ?? v.value),
                label: v.name ?? v.label,
              }))}
              selected={draft.vendor_ids}
              onToggle={(v, c) => toggleMulti('vendor_ids', v, c)}
            />

            <div className="exLabelsField">
              <label htmlFor="mfOrder">Order number</label>
              <input
                id="mfOrder"
                value={draft.order_number || ''}
                onChange={(e) => setField('order_number', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfAwb">AWB</label>
              <input
                id="mfAwb"
                value={draft.awb_number || ''}
                onChange={(e) => setField('awb_number', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfCustomer">Customer name</label>
              <input
                id="mfCustomer"
                value={draft.customer_name || ''}
                onChange={(e) => setField('customer_name', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfPhone">Customer phone</label>
              <input
                id="mfPhone"
                value={draft.customer_phone || ''}
                onChange={(e) => setField('customer_phone', e.target.value)}
              />
            </div>

            <div className="exLabelsField">
              <label htmlFor="mfFrom">Created from</label>
              <input
                id="mfFrom"
                type="date"
                value={draft.date_from || ''}
                onChange={(e) => setField('date_from', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfTo">Created to</label>
              <input
                id="mfTo"
                type="date"
                value={draft.date_to || ''}
                onChange={(e) => setField('date_to', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfDelFrom">Delivered from</label>
              <input
                id="mfDelFrom"
                type="date"
                value={draft.delivered_from || ''}
                onChange={(e) => setField('delivered_from', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="mfDelTo">Delivered to</label>
              <input
                id="mfDelTo"
                type="date"
                value={draft.delivered_to || ''}
                onChange={(e) => setField('delivered_to', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="exLabelsModalFoot">
          <button type="button" className="exLabelsFilterBtn" onClick={() => setDraft({})}>
            Clear
          </button>
          <button type="button" className="exLabelsExportBtn" onClick={apply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// Derived, not a second copy: the toolbar select is the same list plus an
// "all" sentinel, and two hand-maintained copies of a ten-value enum drift.
const STATUS_OPTIONS = [{ value: '', label: 'All statuses' }, ...MANUAL_STATUSES];

function RowMenu({ row, onClone, onCancel, onDownload, busy }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // Rendered through a portal onto <body>, not inside the row.
  //
  // The table sits in a container with `overflow-x: auto`, and an overflow
  // container clips absolutely-positioned descendants — which cropped the menu
  // and forced a scroll inside the row just to reach "Cancel". Fixed coords off
  // the trigger's viewport rect escape that entirely, the same way the
  // exception list does it.
  const place = useCallback(() => {
    const trigger = ref.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const MENU_H = 150;
    // Flip above when there is no room below, so the last row's menu is never
    // pinned off-screen.
    const openUp = rect.bottom + MENU_H > window.innerHeight && rect.top > MENU_H;
    setCoords({
      top: openUp ? rect.top - MENU_H - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDocClick = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // A scroll or resize moves the trigger, so the menu has to follow or close.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const hasDocs = row.has_label || row.has_manifest;
  const cancellable = row.status !== 'cancelled';

  return (
    <div className="manualRowMenu" ref={ref}>
      <button
        type="button"
        className="manualRowMenuTrigger"
        aria-label={`Actions for ${row.order_number}`}
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && coords && createPortal(
        <div
          className="manualRowMenuList"
          role="menu"
          ref={menuRef}
          style={{ top: coords.top, right: coords.right }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={!hasDocs}
            title={hasDocs ? undefined : 'Documents are still being prepared'}
            onClick={() => { setOpen(false); onDownload(row); }}
          >
            <Download size={16} aria-hidden="true" />
            Label &amp; Manifest
          </button>
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onClone(row); }}>
            <Copy size={16} aria-hidden="true" />
            Clone
          </button>
          <button
            type="button"
            role="menuitem"
            className="isDanger"
            disabled={!cancellable}
            title={cancellable ? undefined : 'Already cancelled'}
            onClick={() => { setOpen(false); onCancel(row); }}
          >
            <CircleX size={16} aria-hidden="true" />
            Cancel order
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

function ManualOrdersTable({ showToast }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [chargeBearer, setChargeBearer] = useState('');
  const [detail, setDetail] = useState(null);
  useTaraTerminalFocus('open_manual_order', detail && { order_number: detail.order_number });
  const [selected, setSelected] = useState([]);
  // Distinct from `selected`: "every row matching the current filters",
  // including pages never loaded. The export sends it as select_all so the
  // server re-runs the filters rather than trusting a list of ids.
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});

  // Options are only needed once the modal opens, so they are fetched lazily.
  useEffect(() => {
    if (!showFilters || Object.keys(filterOptions).length) return;
    (async () => {
      try {
        const api = await import('../../api/api');
        setFilterOptions((await api.fetchOrderFilterOptions()) || {});
      } catch {
        // A failed options load still leaves the text and date filters usable.
        setFilterOptions({});
      }
    })();
  }, [showFilters, filterOptions]);

  const activeFilterCount = Object.keys(filters).length;

  const [exporting, setExporting] = useState(false);
  const [exportReady, setExportReady] = useState(null);

  /**
   * Build the export, then poll until the file is ready.
   *
   * The job runs server-side, so the button releases immediately and a card
   * appears at the corner when the download exists — the same pattern the
   * Orders tab uses, so a long export never blocks the screen.
   */
  const handleExport = async () => {
    setExporting(true);
    setExportReady(null);
    try {
      // Three shapes, one call:
      //   ticked rows     -> ids, selectAll false
      //   select-all mode -> no ids, selectAll true (server re-runs the filters)
      //   nothing ticked  -> no ids, selectAll true (the whole filtered view)
      const useSelectAll = selectAllMode || selected.length === 0;
      const job = await createExport(
        'manual_orders',
        'xlsx',
        useSelectAll ? [] : selected,
        useSelectAll,
        // Exactly the rows on screen: the same search and filters the list sent.
        { q: search.trim(), status, charge_bearer: chargeBearer, ...filters },
        {},
        null
      );
      const jobId = job?.jobId || job?.job_id || job?.id;
      if (!jobId) throw new Error('Export did not start.');

      // Poll rather than hold the request open; give up after ~2 minutes so a
      // dead worker does not spin forever.
      for (let i = 0; i < 60; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        // eslint-disable-next-line no-await-in-loop
        const st = await getExportStatus(jobId);
        if (st?.status === 'ready' && st?.downloadUrl) {
          setExportReady({ url: st.downloadUrl, filename: st.filename || 'manual_orders.xlsx' });
          return;
        }
        if (st?.status === 'failed') {
          throw new Error(st?.error || 'Export failed.');
        }
      }
      throw new Error('Export is taking longer than expected. Check back shortly.');
    } catch (err) {
      showToast?.(err?.message || 'Could not export.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listManualOrders({
        q: search.trim(),
        // The toolbar's quick selects and the modal's filters share the request;
        // the modal wins where both set the same key.
        status,
        charge_bearer: chargeBearer,
        ...filters,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(data?.orders || []);
      setCount(data?.count || 0);
    } catch (err) {
      setError(err?.message || 'Could not load manual orders.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, status, chargeBearer, filters, offset]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // A changed filter or search means the previous selection refers to rows that
  // may no longer be listed, so it is dropped rather than silently exporting
  // something the admin can no longer see.
  useEffect(() => {
    setSelected([]);
    setSelectAllMode(false);
  }, [search, status, chargeBearer, filters]);

  const toggleRow = (id) => {
    setSelectAllMode(false);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const pageIds = rows.map((r) => r.id);
  const allOnPageSelected = rows.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleAll = () => {
    setSelectAllMode(false);
    setSelected(allOnPageSelected ? [] : pageIds);
  };

  const clearSelection = () => {
    setSelected([]);
    setSelectAllMode(false);
  };

  // What the export will actually cover, for the banner and the button label.
  const selectionCount = selectAllMode ? count : selected.length;

  const handleDownload = async (row) => {
    setBusyId(row.id);
    try {
      // One merged PDF — label then manifest, the order the rider expects.
      const data = await getManualOrderDocuments(row.id);
      if (data?.combined_url) {
        window.open(data.combined_url, '_blank', 'noopener');
        // `documents` names what is actually inside, so a manifest-only file is
        // never announced as a label.
        const inside = (data.documents || []).join(' + ');
        if (inside && inside !== 'label + manifest') {
          showToast?.(`Downloaded ${inside} — the other document was not available.`, 'warning');
        }
      } else {
        showToast?.('Documents are still being prepared. Try again shortly.', 'warning');
      }
      // The endpoint re-fetches anything missing, so the row may have gained one.
      load();
    } catch (err) {
      showToast?.(err?.message || 'Could not fetch documents.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (row) => {
    const reason = window.prompt(
      `Cancel ${row.order_number}?\n\nThe row is kept for audit — a late courier charge still has to resolve to it.\n\nReason (optional):`
    );
    // `null` is the operator dismissing the prompt; an empty string is them
    // confirming without a reason.
    if (reason === null) return;

    setBusyId(row.id);
    try {
      const data = await cancelManualOrder(row.id, reason);
      if (data?.prozo_cancelled === false && row.awb_number) {
        // Said plainly: our row is cancelled but the AWB may still be live.
        showToast?.(
          `Cancelled locally, but Prozo refused: ${data.prozo_error || 'unknown error'}`,
          'warning'
        );
      } else {
        showToast?.(`${row.order_number} cancelled.`, 'success');
      }
      load();
    } catch (err) {
      showToast?.(err?.error || err?.message || 'Could not cancel.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleClone = async (row) => {
    setBusyId(row.id);
    try {
      const context = await fetchManualOrderCloneContext(row.id);
      // Handed to the wizard through navigation state: it is a prefill, not a
      // saved draft, so it deliberately does not go in the URL. The operator
      // walks the same steps and nothing is written until they submit.
      navigate('/suspire-terminal/manual-orders/new', { state: { cloneFrom: context } });
    } catch (err) {
      showToast?.(err?.error || err?.message || 'Could not load that order.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="exLabelsWrap">
      <div className="exLabelsToolbar">
        <input
          type="search"
          className="exLabelsSearch"
          placeholder="Search order number, AWB or customer"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
        />
        <select
          className="exLabelsSelect"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="exLabelsSelect"
          value={chargeBearer}
          onChange={(e) => { setChargeBearer(e.target.value); setOffset(0); }}
        >
          <option value="">All bearers</option>
          <option value="SUSPIRE">Suspire</option>
          <option value="BRAND">Brand</option>
        </select>
        <div className="exLabelsToolbarRight">
          <button
            type="button"
            className={`exLabelsFilterBtn ${activeFilterCount ? 'hasActive' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="exLabelsFilterBadge">{activeFilterCount}</span>
            )}
          </button>
          <span className="exLabelsCount">
            {selectionCount > 0 ? `${selectionCount} selected` : `${count} orders`}
          </span>
          <button
            type="button"
            className="exLabelsExportBtn"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting
              ? 'Exporting…'
              : selectionCount > 0 && !selectAllMode
                ? `Export ${selectionCount}`
                : 'Export'}
          </button>
        </div>
      </div>

      {/* Offers the rows the admin cannot see. Without it "select all" silently
          means "this page only", which is the wrong answer for an export. */}
      {allOnPageSelected && count > rows.length && (
        <div className="exLabelsSelectAllBar">
          {selectAllMode ? (
            <>
              <span>All <strong>{count}</strong> matching orders are selected.</span>
              <button type="button" onClick={clearSelection}>Clear selection</button>
            </>
          ) : (
            <>
              <span>All <strong>{rows.length}</strong> on this page are selected.</span>
              <button type="button" onClick={() => setSelectAllMode(true)}>
                Select all {count} matching orders
              </button>
            </>
          )}
        </div>
      )}

      {/* Applied filters as removable chips, so what is narrowing the list is
          visible without reopening the modal. */}
      {activeFilterCount > 0 && (
        <div className="exLabelsChips">
          {Object.entries(filters).map(([key, value]) => (
            <span key={key} className="exLabelsChip">
              {FILTER_LABELS[key] || key}: {String(value).replace(/,/g, ', ')}
              <button
                type="button"
                onClick={() => {
                  const next = { ...filters };
                  delete next[key];
                  setOffset(0);
                  setFilters(next);
                }}
                aria-label={`Remove ${FILTER_LABELS[key] || key} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className="exLabelsClearChips"
            onClick={() => { setOffset(0); setFilters({}); }}
          >
            Clear all
          </button>
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="exLabelsChips">
          {Object.entries(filters).map(([key, value]) => (
            <span key={key} className="exLabelsChip">
              {FILTER_LABELS[key] || key}: {String(value).replace(/,/g, ', ')}
              <button
                type="button"
                onClick={() => {
                  const next = { ...filters };
                  delete next[key];
                  setOffset(0);
                  setFilters(next);
                }}
                aria-label={`Remove ${FILTER_LABELS[key] || key} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className="exLabelsClearChips"
            onClick={() => { setOffset(0); setFilters({}); }}
          >
            Clear all
          </button>
        </div>
      )}

      {error && <p className="exLabelsError">{error}</p>}

      <div className="exLabelsTableScroll manualTableScroll">
        <table className="exLabelsTable manualTable">
          <thead>
            <tr>
              <th className="exLabelsCheckCol">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleAll}
                  aria-label="Select all rows on this page"
                />
              </th>
              <th className="moColOrder">Order</th>
              <th className="moColRoute">Customer &amp; route</th>
              <th className="moColAwb">AWB</th>
              <th className="moColMoney">Payment</th>
              <th className="moColBearer">Charge to</th>
              <th className="moColStatus">Status</th>
              <th className="moColActions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="exLabelsEmpty">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="exLabelsEmpty">
                  No manual orders yet. Use <strong>+ New Manual Order</strong> to add one.
                </td>
              </tr>
            )}
            {!loading && rows.map((row) => (
              <tr key={row.id} onClick={() => setDetail(row)} className="manualRow">
                <td className="exLabelsCheckCol" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectAllMode || selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                    aria-label={`Select ${row.order_number}`}
                  />
                </td>
                {/* Order: number, clone tag, who raised it and why, plus the
                    folded bearer at narrow widths — mirrors how the exception
                    list stacks one column instead of five. */}
                <td className="moColOrder">
                  <div className="exLabelsCellStack">
                    <div className="exLabelsCellTop">
                      <span className="moOrderNo">{row.order_number}</span>
                      {row.cloned_from && <span className="moTag">clone</span>}
                    </div>
                    <div className="exLabelsCellSub moNoteLine" title={row.notes}>
                      {row.notes || <span className="exLabelsNoteMissing">no reason recorded</span>}
                    </div>
                    {row.created_by_name && (
                      <div className="exLabelsCellMuted">by {row.created_by_name}</div>
                    )}
                    {/* Under the order rather than in its own column, matching
                        the exception list: it is a property of the order, not a
                        value anyone scans a whole column of. */}
                    <div className="exLabelsCellMuted">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString(undefined, {
                            dateStyle: 'medium', timeStyle: 'short',
                          })
                        : '—'}
                    </div>
                    <span className="moSub moFold moFoldBearer">
                      {row.charge_bearer === 'BRAND'
                        ? `Brand · ${row.vendor_name || '—'}`
                        : 'Suspire'}
                    </span>
                  </div>
                </td>

                {/* Customer and both ends of the journey, labelled the way the
                    exception list labels them. */}
                <td className="moColRoute">
                  <div className="exLabelsCellStack">
                    <div className="moCustomer">{row.customer_name}</div>
                    <div className="exLabelsCellSub">{row.customer_phone}</div>
                    <div className="exLabelsCellSub">
                      <strong>Pickup:</strong> {row.pickup_contact_name || '—'}
                      {row.pickup_address?.pincode ? `, ${row.pickup_address.pincode}` : ''}
                    </div>
                    <div className="exLabelsCellSub">
                      <strong>Delivery:</strong> {row.shipping_address?.city || '—'}
                      {row.shipping_address?.pincode ? `, ${row.shipping_address.pincode}` : ''}
                    </div>
                    <span className="moSub moFold moFoldAwb">
                      {row.awb_number || 'No AWB yet'}
                    </span>
                  </div>
                </td>

                <td className="moColAwb" onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsCellStack">
                    {row.awb_number ? (
                      row.tracking_link ? (
                        <a
                          className="exLabelsAwb"
                          href={row.tracking_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Track this shipment"
                        >
                          {row.awb_number}
                        </a>
                      ) : (
                        <span className="exLabelsAwb">{row.awb_number}</span>
                      )
                    ) : (
                      <span className="moMuted">Not generated</span>
                    )}
                    <div className="exLabelsCellSub">
                      {row.shipping_partner || row.carrier_name || 'Unknown partner'}
                    </div>
                    {row.courier_channel && (
                      <div className="exLabelsCellMuted">{row.courier_channel}</div>
                    )}
                  </div>
                </td>

                {/* Payment, value and parcel in one column — the exception list
                    groups package the same way. */}
                <td className="moColMoney">
                  <div className="exLabelsCellStack">
                    <span className={`moPayPill ${row.payment_mode === 'COD' ? 'isCod' : 'isPrepaid'}`}>
                      {row.payment_mode}
                    </span>
                    <div className="moAmount">
                      ₹{Number(
                        row.payment_mode === 'COD' ? row.cod_amount || 0 : row.invoice_value || 0
                      ).toFixed(2)}
                    </div>
                    <div className="exLabelsCellMuted">
                      {row.weight_g ? `${row.weight_g} g` : '—'}
                      {row.length_cm
                        ? ` · ${row.length_cm}×${row.breadth_cm}×${row.height_cm} cm`
                        : ''}
                    </div>
                    <div className="exLabelsCellMuted">
                      {(row.line_items || []).length} item
                      {(row.line_items || []).length === 1 ? '' : 's'}
                      {row.declared_total ? ` · ₹${row.declared_total}` : ''}
                    </div>
                  </div>
                </td>

                <td className="moColBearer">
                  <div className="exLabelsCellStack">
                    {row.charge_bearer === 'BRAND' ? (
                      <>
                        <span className="moBearer isBrand">Brand</span>
                        <div className="exLabelsCellSub">{row.vendor_name || '—'}</div>
                      </>
                    ) : (
                      <span className="moBearer isSuspire">Suspire</span>
                    )}
                  </div>
                </td>

                <td className="moColStatus" onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsCellStack">
                    {/* Order status on top, the carrier's own last scan beneath
                        it: two different questions, so two lines. The badge is
                        the link to the courier — it is the thing an operator
                        reaches for, and it stays clickable whatever the scan
                        line happens to say. */}
                    {row.tracking_link ? (
                      <a
                        className={`orderStatusBadge ${manualOrderStatusClass(row.status)}`}
                        href={row.tracking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Track on the courier's site"
                      >
                        {manualOrderStatusLabel(row)}
                      </a>
                    ) : (
                      <span className={`orderStatusBadge ${manualOrderStatusClass(row.status)}`}>
                        {manualOrderStatusLabel(row)}
                      </span>
                    )}
                    {manualOrderProgressLine(row) && (
                      <div className="exLabelsCellSub">{manualOrderProgressLine(row)}</div>
                    )}
                    {row.delivered_at && (
                      <div className="exLabelsCellMuted">
                        Delivered {new Date(row.delivered_at).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short',
                        })}
                      </div>
                    )}
                  </div>
                </td>

                <td className="moColActions" onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsActions">
                    <button
                      type="button"
                      className="exLabelsDetailsBtn"
                      onClick={() => setDetail(row)}
                    >
                      Details
                    </button>
                    <RowMenu
                      row={row}
                      busy={busyId === row.id}
                      onClone={handleClone}
                      onCancel={handleCancel}
                      onDownload={handleDownload}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="exLabelsPager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <span>Page {page} of {pages}</span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= count}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}

      <FiltersModal
        open={showFilters}
        applied={filters}
        options={filterOptions}
        onClose={() => setShowFilters(false)}
        onApply={(next) => { setOffset(0); setFilters(next); setShowFilters(false); }}
      />

      {detail && <ManualOrderDetail row={detail} onClose={() => setDetail(null)} />}

      {/* Pinned rather than inline: the export finishes long after the click,
          by which time the operator has usually scrolled elsewhere. */}
      {exportReady && (
        <div className="exportReadyCard" role="status">
          <div className="exportReadyText">
            <div className="exportReadyTitle">Export ready</div>
            <div className="exportReadyName">{exportReady.filename}</div>
          </div>
          <a
            className="exLabelsExportBtn"
            href={exportReady.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setExportReady(null)}
          >
            Download
          </a>
          <button
            type="button"
            className="exportReadyClose"
            onClick={() => setExportReady(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function ManualOrderDetail({ row, onClose }) {
  return (
    <div className="manualDetailBackdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="manualDetailPanel" onClick={(e) => e.stopPropagation()}>
        <header className="manualDetailHeader">
          <div>
            <h3>{row.order_number}</h3>
            <span className={`orderStatusBadge moModalStatus ${manualOrderStatusClass(row.status)}`}>
              {manualOrderStatusLabel(row)}
            </span>
            {manualOrderProgressLine(row) && (
              <div className="exLabelsCellSub">{manualOrderProgressLine(row)}</div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        {/* The reason this order exists comes first: it is what an accounts or
            ops reader is here for. */}
        {row.notes && (
          <section className="manualDetailNote">
            <h4>Notes</h4>
            <p>{row.notes}</p>
          </section>
        )}

        <section className="manualDetailGrid">
          <div><span>Customer</span><strong>{row.customer_name}</strong></div>
          <div><span>Phone</span><strong>{row.customer_phone}</strong></div>
          <div><span>Payment</span><strong>{row.payment_mode}</strong></div>
          {row.payment_mode === 'COD' && (
            <div><span>COD amount</span><strong>₹{row.cod_amount}</strong></div>
          )}
          <div><span>Invoice value</span><strong>₹{row.invoice_value}</strong></div>
          <div><span>Declared total</span><strong>₹{row.declared_total}</strong></div>
          <div>
            <span>Charge to</span>
            <strong>{row.charge_bearer === 'BRAND' ? `Brand — ${row.vendor_name || '—'}` : 'Suspire'}</strong>
          </div>
          <div><span>AWB</span><strong>{row.awb_number || '—'}</strong></div>
          <div><span>Carrier</span><strong>{row.carrier_name || '—'}</strong></div>
          <div>
            <span>Package</span>
            <strong>{row.weight_g} g · {row.length_cm}×{row.breadth_cm}×{row.height_cm} cm</strong>
          </div>
        </section>

        <section className="manualDetailAddresses">
          <div>
            <h4>Ship from</h4>
            <p>
              {row.pickup_contact_name}<br />
              {row.pickup_address?.address_line_1}<br />
              {row.pickup_address?.city}, {row.pickup_address?.state} {row.pickup_address?.pincode}
            </p>
          </div>
          <div>
            <h4>Ship to</h4>
            <p>
              {row.customer_name}<br />
              {row.shipping_address?.address_line_1}<br />
              {row.shipping_address?.city}, {row.shipping_address?.state} {row.shipping_address?.pincode}
            </p>
          </div>
        </section>

        <section>
          <h4>Line items</h4>
          <table className="manualDetailLines">
            <thead>
              <tr><th>SKU</th><th>Product</th><th>Qty</th><th>Unit</th><th>Total</th></tr>
            </thead>
            <tbody>
              {(row.line_items || []).map((li) => (
                <tr key={li.id}>
                  <td>{li.sku}</td>
                  <td>{li.product_name}</td>
                  <td>{li.quantity}</td>
                  <td>₹{li.unit_price}</td>
                  <td>₹{li.line_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {row.cancel_reason && (
          <section className="manualDetailNote">
            <h4>Cancelled</h4>
            <p>{row.cancel_reason}</p>
          </section>
        )}
      </div>
    </div>
  );
}

export default ManualOrdersTable;
